import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import { UpdatedProfileData } from './updated-profile-data';

type TargetEmail = { email: string };

@Injectable()
export class ValidationService {
  private optionsLoaded = false;

  constructor() {}

  async loadOptions() {
    const zxcvbnCommonPackage = await import(
      /* webpackChunkName: "zxcvbnCommonPackage" */ '@zxcvbn-ts/language-common'
    );
    const zxcvbnEnPackage = await import(/* webpackChunkName: "zxcvbnEnPackage" */ '@zxcvbn-ts/language-en');
    const zxcvbnDePackage = await import(/* webpackChunkName: "zxcvbnDePackage" */ '@zxcvbn-ts/language-de');

    zxcvbnOptions.setOptions({
      dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEnPackage.dictionary,
        ...zxcvbnDePackage.dictionary,
      },
      graphs: zxcvbnCommonPackage.adjacencyGraphs,
      translations: {
        ...zxcvbnDePackage.translations,
        warnings: {
          ...zxcvbnDePackage.translations.warnings,
          userInputs: 'Es sollten keine personenbezogenen Daten vorkommen.',
        },
      },
    });

    this.optionsLoaded = true;
  }

  targetExistsValidator(targets: TargetEmail[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue: string = control.value;
      const targetExists: boolean = this.getTargetExists(targets, controlValue);
      return targetExists ? { targetExists: { value: control.value } } : null;
    };
  }

  getTargetExists(targets: TargetEmail[], controlValue: string) {
    return targets.filter((t) => t.email === controlValue.toString()).length > 0;
  }

  passwordMatchValidator(
    passwordControlName: string = 'password',
    confirmPasswordControlName: string = 'confirmPassword'
  ): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const password = formGroup.get(passwordControlName)?.value;
      const confirmPassword = formGroup.get(confirmPasswordControlName)?.value;
      return password && confirmPassword && password !== confirmPassword ? { passwordMismatch: true } : null;
    };
  }

  passwordStrengthValidator(otherInputs?: string[]): AsyncValidatorFn {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      const value = control.value;
      if (!value) return null;

      if (!this.optionsLoaded) await this.loadOptions();

      const filteredInputs = otherInputs?.filter((input) => input && input.trim()) || [];
      const result = zxcvbn(value, filteredInputs);

      return result.score < 3 ? { weakPassword: result.feedback.warning || 'Das Passwort ist zu schwach.' } : null;
    };
  }

  dateTimeValidator(minDate?: string, maxDate?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      const date = new Date(value);
      const min = minDate ? new Date(minDate) : null;
      const max = maxDate ? new Date(maxDate) : null;

      if (isNaN(date.valueOf())) return { dateTime: true };
      if (min && date < min) return { minDate: true };
      if (max && date > max) return { maxDate: true };
      return null;
    };
  }

  validateProfileFieldLength(updatedProfile: UpdatedProfileData): { valid: boolean; errors: string[] } {
    const maxLength = 200;
    const errors: string[] = [];
    const fields = [
      { name: 'Nachname', value: updatedProfile.name },
      { name: 'Vorname', value: updatedProfile.surname },
      { name: 'Unternehmen', value: updatedProfile.company },
      { name: 'Vorname der Kontaktperson', value: updatedProfile.contactPersonFirstName },
      { name: 'Nachname der Kontaktperson', value: updatedProfile.contactPersonLastName },
      { name: 'E-Mail der Kontaktperson', value: updatedProfile.contactPersonEmail },
    ];

    fields.forEach((field) => {
      if (field.value && field.value.length > maxLength) {
        errors.push(`Das Feld ${field.name} darf maximal ${maxLength} Zeichen haben (aktuell: ${field.value.length})`);
      }
    });

    return { valid: errors.length === 0, errors };
  }
}
